import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useWebSocket } from '../WebSocketContext';
import { Tooltip, Box, CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import EmailIcon from '@mui/icons-material/Email';
import ArchiveIcon from '@mui/icons-material/Archive';
import ArticleIcon from '@mui/icons-material/Article';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DataObjectIcon from '@mui/icons-material/DataObject';
import PDFViewer from '../Viewer/PDFViewer';
import ImageViewer from '../Viewer/ImageViewer';
import OtherViewer from '../Viewer/OtherViewer';
import AudioViewer from '../Viewer/AudioViewer';
import ExcelViewer from '../Viewer/ExcelViewer';
import HTMLViewer from '../Viewer/HTMLViewer';
import TextViewer from '../Viewer/TextViewer';
import VideoViewer from '../Viewer/VideoViewer';
import EmailViewer from '../Viewer/EmailViewer';
import ZipViewer from '../Viewer/ZipViewer';
import WordViewer from '../Viewer/WordViewer';
import PowerPointViewer from '../Viewer/PowerPointViewer';
import CSVViewer from '../Viewer/CSVViewer';
import JSONViewer from '../Viewer/JSONViewer';

// Theme colors to match the rest of the application
const primaryColor = '#5757b0';
const lightPurple = '#f0f0ff';

// Styled thumbnail container with enhanced visual appeal
const StyledThumbnailContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExist' && prop !== 'isHidden',
})(({ theme, isExist, isHidden }) => ({
  position: 'relative',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  border: `2px solid ${isExist ? `${primaryColor}20` : '#ff666620'}`,
  boxShadow: isExist 
    ? '0 4px 16px rgba(87, 87, 176, 0.15)' 
    : '0 2px 8px rgba(255, 102, 102, 0.15)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  cursor: isExist ? 'pointer' : 'not-allowed',
  filter: !isExist || isHidden ? 'grayscale(80%)' : 'none',
  opacity: !isExist ? 0.6 : (isHidden ? 0.3 : 1),
  '&:hover': isExist ? {
    transform: 'translateY(-2px) scale(1.02)',
    boxShadow: '0 8px 24px rgba(87, 87, 176, 0.25)',
    borderColor: primaryColor,
    '& .thumbnail-overlay': {
      opacity: 1,
    },
    '& .thumbnail-icon': {
      transform: 'scale(1.1)',
    },
  } : {},
  '&:active': isExist ? {
    transform: 'translateY(-1px) scale(1.01)',
  } : {},
}));

// Styled image container for better image presentation
const StyledImageContainer = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
    transition: 'transform 0.3s ease',
  },
});

// Overlay for hover effects
const ThumbnailOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, rgba(87, 87, 176, 0.8) 0%, rgba(119, 119, 208, 0.6) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  borderRadius: '10px',
});

// Loading indicator
const LoadingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  background: `linear-gradient(135deg, ${lightPurple} 0%, #ffffff 100%)`,
});

const Thumbnail = ({timeevent, sx}) => {
  const { id, isExist, hide, eventsource } = timeevent;
  
  // Debug logging to see what properties are available
  //console.log('Thumbnail timeevent props:', timeevent);

  const { ws, subscribeToMessages } = useWebSocket();
  const [blobUrl, setBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const thumbnailRef = useRef(null);
  const [thumbnailRequested, setThumbnailRequested] = useState(false);
  
  // PDF viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('');
  
  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageFilename, setImageFilename] = useState('');
  
  // Other viewer state
  const [otherViewerOpen, setOtherViewerOpen] = useState(false);
  const [otherFilename, setOtherFilename] = useState('');
  
  // Audio viewer state
  const [audioViewerOpen, setAudioViewerOpen] = useState(false);
  const [audioFilename, setAudioFilename] = useState('');
  
  // Excel viewer state
  const [excelViewerOpen, setExcelViewerOpen] = useState(false);
  const [excelFilename, setExcelFilename] = useState('');
  
  // HTML viewer state
  const [htmlViewerOpen, setHtmlViewerOpen] = useState(false);
  const [htmlFilename, setHtmlFilename] = useState('');
  
  // Text viewer state
  const [textViewerOpen, setTextViewerOpen] = useState(false);
  const [textFilename, setTextFilename] = useState('');
  
  // Video viewer state
  const [videoViewerOpen, setVideoViewerOpen] = useState(false);
  const [videoFilename, setVideoFilename] = useState('');
  
  // Email viewer state
  const [emailViewerOpen, setEmailViewerOpen] = useState(false);
  const [emailFilename, setEmailFilename] = useState('');
  
  // Zip viewer state
  const [zipViewerOpen, setZipViewerOpen] = useState(false);
  const [zipFilename, setZipFilename] = useState('');
  
  // Word viewer state
  const [wordViewerOpen, setWordViewerOpen] = useState(false);
  const [wordFilename, setWordFilename] = useState('');
  
  // PowerPoint viewer state
  const [powerpointViewerOpen, setPowerpointViewerOpen] = useState(false);
  const [powerpointFilename, setPowerpointFilename] = useState('');
  
  // CSV viewer state
  const [csvViewerOpen, setCsvViewerOpen] = useState(false);
  const [csvFilename, setCsvFilename] = useState('');
  
  // JSON viewer state
  const [jsonViewerOpen, setJsonViewerOpen] = useState(false);
  const [jsonFilename, setJsonFilename] = useState('');
  
  // Function to check if file is an audio format
  const isAudioFormat = useCallback(() => {
    if (!eventsource) return false;
    const audioExtensions = ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac'];
    const lowered = eventsource.toLowerCase();
    return audioExtensions.some(ext => lowered.endsWith(ext));
  }, [eventsource]);

  // Function to check if file is a PDF
  const isPDFFormat = useCallback(() => {
    if (!eventsource) {
      console.log('No eventsource available for event:', id);
      return false;
    }
    const isPdf = eventsource.toLowerCase().endsWith('.pdf');
    //console.log('Checking if PDF:', eventsource, 'Result:', isPdf);
    return isPdf;
  }, [eventsource, id]);

  // Function to check if file is an image
  const isImageFormat = useCallback(() => {
    if (!eventsource) {
      console.log('No eventsource available for event:', id);
      return false;
    }
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg'];
    const isImage = imageExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
    return isImage;
  }, [eventsource, id]);

  // Function to check if file is an Excel format
  const isExcelFormat = useCallback(() => {
    if (!eventsource) return false;
    const excelExtensions = ['.xlsx', '.xls', '.xlsm'];
    return excelExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is HTML
  const isHTMLFormat = useCallback(() => {
    if (!eventsource) return false;
    const htmlExtensions = ['.html', '.htm'];
    return htmlExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is a text format
  const isTextFormat = useCallback(() => {
    if (!eventsource) return false;
    const textExtensions = ['.txt', '.ini', '.hl7', '.log', '.md', '.json', '.xml', '.js', '.py', '.java', '.cpp', '.cs', '.php', '.rb', '.go', '.rs', '.swift', '.kt', '.ts', '.jsx', '.tsx', '.vue', '.css', '.scss', '.sass', '.less', '.sql', '.sh', '.bat', '.ps1', '.yaml', '.yml', '.toml', '.cfg', '.conf', '.properties'];
    return textExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is a video format
  const isVideoFormat = useCallback(() => {
    if (!eventsource) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.m4v', '.3gp', '.m3u8'];
    return videoExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is an email format
  const isEmailFormat = useCallback(() => {
    if (!eventsource) return false;
    const emailExtensions = ['.msg', '.eml'];
    return emailExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is a zip format
  const isZipFormat = useCallback(() => {
    if (!eventsource) return false;
    const zipExtensions = ['.zip', '.rar', '.7z', '.tar', '.gz', '.bz2'];
    return zipExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is a Word format
  const isWordFormat = useCallback(() => {
    if (!eventsource) return false;
    const wordExtensions = ['.docx', '.doc'];
    return wordExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is a PowerPoint format
  const isPowerPointFormat = useCallback(() => {
    if (!eventsource) return false;
    const pptExtensions = ['.pptx', '.ppt'];
    return pptExtensions.some(ext => eventsource.toLowerCase().endsWith(ext));
  }, [eventsource]);

  // Function to check if file is a CSV format
  const isCSVFormat = useCallback(() => {
    if (!eventsource) return false;
    return eventsource.toLowerCase().endsWith('.csv');
  }, [eventsource]);

  // Function to check if file is a JSON format
  const isJSONFormat = useCallback(() => {
    if (!eventsource) return false;
    return eventsource.toLowerCase().endsWith('.json');
  }, [eventsource]);

  const openFile = useCallback(() => {
    if (isExist) {
      const filename = eventsource ? eventsource.split('\\').pop().split('/').pop() : 'document';
      
      if (isPDFFormat()) {
        console.log('Opening PDF file:', filename, 'for event ID:', id);
        setPdfFilename(filename);
        setPdfViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isImageFormat()) {
        console.log('Opening Image file:', filename, 'for event ID:', id);
        setImageFilename(filename);
        setImageViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isAudioFormat()) {
        console.log('Opening Audio file:', filename, 'for event ID:', id);
        setAudioFilename(filename);
        setAudioViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isExcelFormat()) {
        console.log('Opening Excel file:', filename, 'for event ID:', id);
        setExcelFilename(filename);
        setExcelViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isHTMLFormat()) {
        console.log('Opening HTML file:', filename, 'for event ID:', id);
        setHtmlFilename(filename);
        setHtmlViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isVideoFormat()) {
        console.log('Opening Video file:', filename, 'for event ID:', id);
        setVideoFilename(filename);
        setVideoViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isEmailFormat()) {
        console.log('Opening Email file:', filename, 'for event ID:', id);
        setEmailFilename(filename);
        setEmailViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isZipFormat()) {
        console.log('Opening Zip file:', filename, 'for event ID:', id);
        setZipFilename(filename);
        setZipViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isWordFormat()) {
        console.log('Opening Word file:', filename, 'for event ID:', id);
        setWordFilename(filename);
        setWordViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isPowerPointFormat()) {
        console.log('Opening PowerPoint file:', filename, 'for event ID:', id);
        setPowerpointFilename(filename);
        setPowerpointViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isCSVFormat()) {
        console.log('Opening CSV file:', filename, 'for event ID:', id);
        setCsvFilename(filename);
        setCsvViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isJSONFormat()) {
        console.log('Opening JSON file:', filename, 'for event ID:', id);
        setJsonFilename(filename);
        setJsonViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else if (isTextFormat()) {
        console.log('Opening Text file:', filename, 'for event ID:', id);
        setTextFilename(filename);
        setTextViewerOpen(true);
        ws.send(JSON.stringify({ command: 'openfile', id: id }));
      } else {
        // For any other files, use the Other viewer
        console.log('Opening Other file:', filename, 'for event ID:', id);
        setOtherFilename(filename);
        setOtherViewerOpen(true);
        // Note: We don't send the openfile command here as OtherViewer handles it internally
      }
    }
  }, [ws, id, isExist, isPDFFormat, isImageFormat, isAudioFormat, isExcelFormat, isHTMLFormat, isVideoFormat, isEmailFormat, isZipFormat, isWordFormat, isPowerPointFormat, isCSVFormat, isJSONFormat, isTextFormat, eventsource]);  // Combined effect for intersection observer and WebSocket handling
  useEffect(() => {
    if (!isExist || isAudioFormat()) return;

    const watchedCommands = ["getThumbnail"];
    const handleThumbnailResponse = (parsedMessage = {}) => {
      const { command, data = {} } = parsedMessage;
      if (command !== "getThumbnail" || String(data.id) !== String(id)) {
        return;
      }

      setIsLoading(false);
      setThumbnailRequested(true);
      try {
        const binaryString = window.atob(data.data);
        const binaryLen = binaryString.length;
        const bytes = new Uint8Array(binaryLen);
        for (let i = 0; i < binaryLen; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (error) {
        console.error('Error processing thumbnail data:', error);
        setIsLoading(false);
      }
    };
    const unsubscribe = subscribeToMessages(watchedCommands, handleThumbnailResponse);

    // Set up intersection observer
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !thumbnailRequested && !blobUrl) {
          setIsLoading(true);
          setThumbnailRequested(true);
          
          // Add a small delay to ensure WebSocket subscription is ready
          setTimeout(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              const messageJson = JSON.stringify({ command: 'getthumbnail', id: id });
              ws.send(messageJson);
            } else {
              // Retry if WebSocket is not ready
              const retryInterval = setInterval(() => {
                if (ws && ws.readyState === WebSocket.OPEN) {
                  const messageJson = JSON.stringify({ command: 'getthumbnail', id: id });
                  ws.send(messageJson);
                  clearInterval(retryInterval);
                }
              }, 100);
              
              // Clear retry after 5 seconds to avoid infinite retry
              setTimeout(() => clearInterval(retryInterval), 5000);
            }
          }, 50);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const currentRef = thumbnailRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      unsubscribe();
      // Clean up blob URL to prevent memory leaks
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [id, ws, subscribeToMessages, isExist, isAudioFormat, thumbnailRequested, blobUrl]);

  const pdfViewerRef = useRef(null);
  return (
    <>
      <Tooltip 
        title={
          <Box sx={{ p: 1 }}>
            <Box sx={{ fontWeight: '500', mb: 0.5 }}>
              {isExist ? (
                isPDFFormat() ? "Click to view PDF" : 
                isImageFormat() ? "Click to view image" : 
                isAudioFormat() ? "Click to play audio" :
                isExcelFormat() ? "Click to view Excel" :
                isHTMLFormat() ? "Click to view HTML" :
                isTextFormat() ? "Click to view text" :
                isVideoFormat() ? "Click to play video" :
                isEmailFormat() ? "Click to view email" :
                isZipFormat() ? "Click to browse archive" :
                isWordFormat() ? "Click to view Word document" :
                isPowerPointFormat() ? "Click to view presentation" :
                isCSVFormat() ? "Click to view CSV data" :
                isJSONFormat() ? "Click to view JSON data" :
                "Click to open file"
              ) : "File no longer exists"}
            </Box>
            <Box sx={{ fontSize: '0.75rem', opacity: 0.9 }}>
              {isAudioFormat() ? "Audio File" : 
               isPDFFormat() ? "PDF Document" : 
               isImageFormat() ? "Image File" : 
               isExcelFormat() ? "Excel Spreadsheet" :
               isHTMLFormat() ? "HTML Document" :
               isTextFormat() ? "Text File" :
               isVideoFormat() ? "Video File" :
               isEmailFormat() ? "Email Message" :
               isZipFormat() ? "Archive File" :
               isWordFormat() ? "Word Document" :
               isPowerPointFormat() ? "PowerPoint Presentation" :
               isCSVFormat() ? "CSV Data" :
               isJSONFormat() ? "JSON Data" :
               "Document"}
            </Box>
          </Box>
        }
        placement="top"
        arrow
      >
        <StyledThumbnailContainer
          ref={thumbnailRef}
          isExist={isExist}
          isHidden={hide === 1}
          onClick={openFile}
          sx={sx}
        >        <StyledImageContainer>
            {isLoading && !isAudioFormat() ? (
              <LoadingContainer>
                <CircularProgress 
                  size={24} 
                  sx={{ color: primaryColor }} 
                />
              </LoadingContainer>
            ) : blobUrl ? (
              <>
                <img 
                  src={blobUrl} 
                  alt="File Thumbnail" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '10px',
                  }}
                />
                <ThumbnailOverlay className="thumbnail-overlay">
                  <IconButton 
                    className="thumbnail-icon"
                    sx={{ 
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </ThumbnailOverlay>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 2,
                    height: '100%',
                  }}
                >
                  {isAudioFormat() ? (
                    <>
                      <VolumeUpIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: primaryColor, 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: primaryColor,
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Audio
                      </Box>
                    </>
                  ) : isPDFFormat() ? (
                    <>
                      <PictureAsPdfIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#d32f2f', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#d32f2f',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        PDF
                      </Box>
                    </>
                  ) : isImageFormat() ? (
                    <>
                      <ImageIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#4caf50', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#4caf50',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Image
                      </Box>
                    </>
                  ) : isExcelFormat() ? (
                    <>
                      <TableChartIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#2e7d32', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#2e7d32',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Excel
                      </Box>
                    </>
                  ) : isHTMLFormat() ? (
                    <>
                      <CodeIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#ff9800', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#ff9800',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        HTML
                      </Box>
                    </>
                  ) : isTextFormat() ? (
                    <>
                      <TextFieldsIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#795548', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#795548',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Text
                      </Box>
                    </>
                  ) : isVideoFormat() ? (
                    <>
                      <PlayCircleIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#e91e63', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#e91e63',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Video
                      </Box>
                    </>
                  ) : isEmailFormat() ? (
                    <>
                      <EmailIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#3f51b5', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#3f51b5',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Email
                      </Box>
                    </>
                  ) : isZipFormat() ? (
                    <>
                      <ArchiveIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#607d8b', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#607d8b',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Archive
                      </Box>
                    </>
                  ) : isWordFormat() ? (
                    <>
                      <ArticleIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#2196f3', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#2196f3',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Word
                      </Box>
                    </>
                  ) : isPowerPointFormat() ? (
                    <>
                      <SlideshowIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#ff5722', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#ff5722',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        PowerPoint
                      </Box>
                    </>
                  ) : isCSVFormat() ? (
                    <>
                      <TableChartIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#4caf50', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#4caf50',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        CSV
                      </Box>
                    </>
                  ) : isJSONFormat() ? (
                    <>
                      <DataObjectIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: '#9c27b0', 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: '#9c27b0',
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        JSON
                      </Box>
                    </>
                  ) : (
                    <>
                      <DescriptionIcon 
                        sx={{ 
                          fontSize: 32, 
                          color: primaryColor, 
                          mb: 1,
                          opacity: 0.8
                        }} 
                      />
                      <Box
                        sx={{
                          fontSize: '0.6rem',
                          fontWeight: '600',
                          color: primaryColor,
                          textAlign: 'center',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Document
                      </Box>
                    </>
                  )}
                </Box>
                
                {isExist && (
                  <ThumbnailOverlay className="thumbnail-overlay">
                    <IconButton 
                      className="thumbnail-icon"
                      sx={{ 
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        },
                      }}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </ThumbnailOverlay>
                )}
              </>
            )}
          </StyledImageContainer>
          
          {/* Status indicator for file existence */}
          {!isExist && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#ff6666',
                boxShadow: '0 0 0 2px white',
              }}
            />
          )}
          
          {/* Hidden indicator */}
          {hide === 1 && isExist && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#ffa500',
                boxShadow: '0 0 0 2px white',
              }}
            />
          )}
        </StyledThumbnailContainer>
      </Tooltip>

      {/* PDF Viewer */}
      <PDFViewer 
        ref={pdfViewerRef}
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        filename={pdfFilename}
        eventId={id}
      />

      {/* Image Viewer */}
      <ImageViewer 
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        filename={imageFilename}
        eventId={id}
      />

      {/* Other Viewer */}
      <OtherViewer 
        open={otherViewerOpen}
        onClose={() => setOtherViewerOpen(false)}
        filename={otherFilename}
        eventId={id}
        filePath={eventsource}
      />

      {/* Audio Viewer */}
      <AudioViewer 
        open={audioViewerOpen}
        onClose={() => setAudioViewerOpen(false)}
        filename={audioFilename}
        eventId={id}
        timeevent={timeevent}
      />

      {/* Excel Viewer */}
      <ExcelViewer 
        open={excelViewerOpen}
        onClose={() => setExcelViewerOpen(false)}
        filename={excelFilename}
        eventId={id}
      />

      {/* HTML Viewer */}
      <HTMLViewer 
        open={htmlViewerOpen}
        onClose={() => setHtmlViewerOpen(false)}
        filename={htmlFilename}
        eventId={id}
      />

      {/* Text Viewer */}
      <TextViewer 
        open={textViewerOpen}
        onClose={() => setTextViewerOpen(false)}
        filename={textFilename}
        eventId={id}
      />

      {/* Video Viewer */}
      <VideoViewer 
        open={videoViewerOpen}
        onClose={() => setVideoViewerOpen(false)}
        filename={videoFilename}
        eventId={id}
      />

      {/* Email Viewer */}
      <EmailViewer 
        open={emailViewerOpen}
        onClose={() => setEmailViewerOpen(false)}
        filename={emailFilename}
        eventId={id}
      />

      {/* Zip Viewer */}
      <ZipViewer 
        open={zipViewerOpen}
        onClose={() => setZipViewerOpen(false)}
        filename={zipFilename}
        eventId={id}
      />

      {/* Word Viewer */}
      <WordViewer 
        open={wordViewerOpen}
        onClose={() => setWordViewerOpen(false)}
        filename={wordFilename}
        eventId={id}
      />

      {/* PowerPoint Viewer */}
      <PowerPointViewer 
        open={powerpointViewerOpen}
        onClose={() => setPowerpointViewerOpen(false)}
        filename={powerpointFilename}
        eventId={id}
      />

      {/* CSV Viewer */}
      <CSVViewer 
        open={csvViewerOpen}
        onClose={() => setCsvViewerOpen(false)}
        filename={csvFilename}
        eventId={id}
      />

      {/* JSON Viewer */}
      <JSONViewer 
        open={jsonViewerOpen}
        onClose={() => setJsonViewerOpen(false)}
        filename={jsonFilename}
        eventId={id}
      />
    </>
  );
};

export default Thumbnail;
